import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faFileLines,
    faLocationDot,
    faMegaphone,
    faPaperPlane,
    faUsers
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { RecipientEstimate } from '../../audience';
import { CampaignDraft, EmailTemplate } from '../../types';
import { useEmailMarketing } from '../../EmailMarketingProvider';
import { describeAudience, formatNumber } from '../../utils';
import { Panel } from '../../UI/Panel/Panel';
import { RadioCard } from '../../UI/RadioCard/RadioCard';
import StepHeading from './StepHeading';
import styles from './steps.module.scss';

interface ReviewStepProps {
    draft: CampaignDraft;
    estimate: RecipientEstimate;
    template: EmailTemplate | null;
    onEditStep: (step: number) => void;
}

interface ReviewItem {
    icon: IconDefinition;
    label: string;
    value: React.ReactNode;
    detail?: string;
    step: number;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ draft, estimate, template, onEditStep }) => {
    const { describeLocations } = useEmailMarketing();
    const items: Array<ReviewItem> = [
        { icon: faMegaphone, label: 'Campaign', value: draft.name, step: 0 },
        { icon: faLocationDot, label: 'Locations', value: describeLocations(draft.locationIds), step: 0 },
        {
            icon: faPaperPlane,
            label: 'From',
            value: "Each location's verified email",
            detail: `${draft.locationIds.length} sender${draft.locationIds.length === 1 ? '' : 's'}`,
            step: 0
        },
        {
            icon: faUsers,
            label: 'Recipients',
            value: estimate.known ? `${formatNumber(estimate.total)} customers` : 'Counted when sending',
            detail: describeAudience(draft.recipientType, draft.tags, draft),
            step: 1
        },
        { icon: faFileLines, label: 'Template', value: template?.name ?? '—', step: 2 },
        { icon: faEnvelope, label: 'Subject', value: draft.subject, detail: draft.previewText || undefined, step: 2 }
    ];

    return (
        <>
            <StepHeading
                title="Review and send"
                description="Double-check everything before it goes out to your customers."
            />

            <Panel title="Summary">
                <dl className={styles.reviewList}>
                    {items.map(item => (
                        <div
                            key={item.label}
                            className={styles.reviewRow}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                            <dt>{item.label}</dt>
                            <dd>
                                {item.value}
                                {item.detail && <small>{item.detail}</small>}
                            </dd>
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => onEditStep(item.step)}
                            >
                                Edit
                            </button>
                        </div>
                    ))}
                </dl>
            </Panel>

            <Panel title="Send time">
                <div className={styles.radioGrid}>
                    <RadioCard
                        title="Send now"
                        description="Starts sending as soon as you confirm."
                        checked
                        onSelect={() => undefined}
                    />
                    <RadioCard
                        title="Schedule"
                        description="Pick a date and time."
                        badge={<span className={styles.soon}>Coming soon</span>}
                        checked={false}
                        disabled
                        onSelect={() => undefined}
                    />
                </div>
            </Panel>
        </>
    );
};

export default ReviewStep;
